import React, { useState } from "react";
import { useSavings } from "../../context/SavingsContext";

const EntryForm: React.FC = () => {
  const { addEntry } = useSavings();
  const [formData, setFormData] = useState({
    department: "",
    description: "",
    amount: "",
    date: new Date().toISOString().slice(0, 10), // Today's date in YYYY-MM-DD
    type: "one-time",
    vendor: "",
  });
  const [message, setMessage] = useState<{
    text: string;
    type: "success" | "error";
  } | null>(null);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addEntry({
        ...formData,
        amount: parseFloat(formData.amount),
        date: new Date(formData.date).toISOString(),
        type: formData.type as "recurring" | "one-time",
      });

      // Reset form
      setFormData({
        department: "",
        description: "",
        amount: "",
        date: new Date().toISOString().slice(0, 10),
        type: "one-time",
        vendor: "",
      });

      setMessage({
        text: "Savings entry added successfully!",
        type: "success",
      });

      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage(null);
      }, 3000);
    } catch (error) {
      setMessage({ text: "Failed to add entry", type: "error" });
    }
  };

  return (
    <div className="bg-gray-900 p-6 rounded-lg">
      <h2 className="text-2xl font-bold mb-6">Add New Savings Entry</h2>

      {message && (
        <div
          className={`p-4 mb-6 rounded ${
            message.type === "success"
              ? "bg-green-800 text-green-200"
              : "bg-red-800 text-red-200"
          }`}
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="department" className="block mb-2">
              Department:
            </label>
            <input
              type="text"
              id="department"
              name="department"
              value={formData.department}
              onChange={handleChange}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded text-white"
              required
            />
          </div>

          <div>
            <label htmlFor="vendor" className="block mb-2">
              Vendor (Optional):
            </label>
            <input
              type="text"
              id="vendor"
              name="vendor"
              value={formData.vendor}
              onChange={handleChange}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded text-white"
            />
          </div>
        </div>

        <div className="mb-6">
          <label htmlFor="description" className="block mb-2">
            Description:
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className="w-full p-3 bg-gray-800 border border-gray-700 rounded text-white"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label htmlFor="amount" className="block mb-2">
              Amount ($):
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded text-white"
              required
            />
          </div>

          <div>
            <label htmlFor="date" className="block mb-2">
              Date:
            </label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded text-white"
              required
            />
          </div>

          <div>
            <label htmlFor="type" className="block mb-2">
              Type:
            </label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded text-white"
              required
            >
              <option value="one-time">One-Time Saving</option>
              <option value="recurring">Monthly Recurring</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded"
          >
            Add Savings Entry
          </button>
        </div>
      </form>
    </div>
  );
};

export default EntryForm;
